import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { createTemplate, updateTemplate, deleteTemplate, adminCompleteContractTask, listPendingContractApprovals, setContractApproval } from '../services/contractsService';
import { fetchTemplates, fetchInstancesByStatus, invalidateTemplates, invalidateInstances } from '../store/slices/contractsSlice';
import { Grid, Card, CardContent, CardActions, Typography, Button, TextField, Stack, IconButton, Chip, Divider, Alert, ToggleButton, ToggleButtonGroup, LinearProgress, Switch, FormControlLabel, Accordion, AccordionSummary, AccordionDetails, Box, Tabs, Tab } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useExternalData } from '../contexts/ExternalDataContext';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { getUserRole } from '../localStorageWithExpiry';

const emptyTemplate = {
  title: '', slug: '', description: '', priceTokens: 0, rewardTokens: 0, penaltyTokens: 0, deadlineDays: 7,
  // Use datetime-local format for better UX, will convert to Unix on save
  startAtLocal: '', endAtLocal: '',
  expiresAt: '', active: true,
  tasks: [ { order: 1, title: '', criteria: {} } ]
};

export default function AdminContracts() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const canMarkDone = getUserRole() === 'admin';
  const canApprove = getUserRole() === 'admin';
  const {
    templates,
    templatesLoading,
    templatesError,
    instances: storeInstances,
    instancesStatus,
    instancesLoading,
    instancesError,
  } = useSelector((state) => state.contracts);

  const [form, setForm] = useState(emptyTemplate);
  const [editingId, setEditingId] = useState(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [activeTab, setActiveTab] = useState(0);
  const [expandedTaskIdx, setExpandedTaskIdx] = useState(0);
  const [progressVisibleCount, setProgressVisibleCount] = useState(40);
  const [progressSearch, setProgressSearch] = useState('');
  const [expandedProgressId, setExpandedProgressId] = useState(null);
  const [completingTaskKey, setCompletingTaskKey] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [approvalActionId, setApprovalActionId] = useState(null);
  const PROGRESS_PAGE_SIZE = 40;

  const instances = useMemo(
    () => (instancesStatus === statusFilter && Array.isArray(storeInstances) ? storeInstances : []),
    [storeInstances, instancesStatus, statusFilter]
  );

  const { cityOptions, companyOptionsByCity, cargoOptions, ensureCargoOptions } = useExternalData();

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'approvals') setActiveTab(2);
    else if (tab === 'progress') setActiveTab(3);
    else if (tab === 'templates') setActiveTab(1);
  }, [searchParams]);

  useEffect(() => {
    dispatch(fetchTemplates());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchInstancesByStatus(statusFilter));
  }, [dispatch, statusFilter]);

  // AdminContracts needs cargo list for criteria autocompletes.
  useEffect(() => {
    ensureCargoOptions?.();
  }, [ensureCargoOptions]);

  useEffect(() => { setProgressVisibleCount(PROGRESS_PAGE_SIZE); }, [statusFilter]);
  useEffect(() => { setProgressVisibleCount(PROGRESS_PAGE_SIZE); }, [progressSearch]);

  useEffect(() => {
    if (templatesError) setErr(templatesError);
  }, [templatesError]);

  const loadPendingApprovals = async () => {
    setApprovalsLoading(true);
    try {
      const list = await listPendingContractApprovals();
      setPendingApprovals(Array.isArray(list) ? list : []);
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || 'Failed to load pending approvals');
    } finally {
      setApprovalsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 2) loadPendingApprovals();
  }, [activeTab]);

  useEffect(() => {
    loadPendingApprovals();
  }, []);


  const setTask = (idx, patch) => {
    const tasks = [...form.tasks];
    tasks[idx] = { ...tasks[idx], ...patch };
    setForm({ ...form, tasks });
  };

  const addTask = () => setForm({ ...form, tasks: [...form.tasks, { order: form.tasks.length+1, title: '', criteria: {} }] });
  const removeTask = (idx) => {
    setForm({ ...form, tasks: form.tasks.filter((_, i) => i !== idx) });
    setExpandedTaskIdx((prev) => (prev === idx ? 0 : prev > idx ? prev - 1 : prev));
  };

  // Helper function to convert IST datetime to Unix seconds
  const istToUnixSeconds = (istDateTime) => {
    if (!istDateTime) return '';
    // Create date in IST timezone (UTC+5:30)
    const date = new Date(istDateTime + '+05:30');
    return Math.floor(date.getTime() / 1000);
  };

  // Helper function to convert Unix seconds to IST datetime format
  const unixToIstDateTime = (unixSeconds) => {
    if (!unixSeconds) return '';
    const date = new Date(unixSeconds * 1000);
    // Convert to IST (UTC+5:30)
    const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    return istDate.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:MM
  };

  const save = async () => {
    setMsg(''); setErr('');
    try {
      // Convert IST datetime inputs to Unix seconds for backend
      const payload = {
        ...form,
        startAtUnix: istToUnixSeconds(form.startAtLocal),
        endAtUnix: istToUnixSeconds(form.endAtLocal)
      };
      // Remove local datetime fields from payload
      delete payload.startAtLocal;
      delete payload.endAtLocal;

      if (editingId) {
        await updateTemplate(editingId, payload);
        setMsg('Updated');
      } else {
        await createTemplate(payload);
        setMsg('Created — awaiting admin approval before it goes live at the start date/time.');
      }
      setForm(emptyTemplate);
      setEditingId(null);
      dispatch(invalidateTemplates());
      await dispatch(fetchTemplates()).unwrap().catch(() => {});
    } catch(e) { setErr(e.message || 'Save failed'); }
  };

  const edit = (tpl) => {
    setEditingId(tpl._id);
    setExpandedTaskIdx(0);
    setForm({
      title: tpl.title, slug: tpl.slug, description: tpl.description || '', priceTokens: tpl.priceTokens, rewardTokens: tpl.rewardTokens, penaltyTokens: tpl.penaltyTokens, deadlineDays: tpl.deadlineDays,
      startAtLocal: tpl.startAt ? unixToIstDateTime(Math.floor(new Date(tpl.startAt).getTime()/1000)) : '',
      endAtLocal: tpl.endAt ? unixToIstDateTime(Math.floor(new Date(tpl.endAt).getTime()/1000)) : '',
      expiresAt: tpl.expiresAt ? tpl.expiresAt.slice(0,10) : '', active: !!tpl.active,
      tasks: (tpl.tasks||[]).map(t => ({ order: t.order, title: t.title, criteria: t.criteria || {} }))
    });
  };

  const del = async (id) => {
    setErr(''); setMsg('');
    try {
      await deleteTemplate(id);
      dispatch(invalidateTemplates());
      await dispatch(fetchTemplates()).unwrap().catch(() => {});
      setMsg('Deleted');
    } catch(e) { setErr(e.message || 'Delete failed'); }
  };

  const criteriaFields = [
    ['sourceCity','From'], ['sourceCompany','Source Company'], ['destinationCity','To'], ['destinationCompany','Destination Company'], ['cargoName','Cargo'],
    ['minDistance','Min Distance (km)'], ['maxDamagePct','Max Cargo Damage %'],
    ['maxTopSpeedKmh','Max Top Speed (km/h)'], ['minRevenue','Min Revenue'],
    ['minAvgSpeedKmh','Min Avg Speed'], ['maxAvgSpeedKmh','Max Avg Speed'],
    ['maxTruckDamagePercent','Max Truck Damage %'], ['maxTrailerDamagePercent','Max Trailer Damage %']
  ];

  const renderCreateEditTab = () => (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{editingId ? 'Edit Contract Template' : 'Create Contract Template'}</Typography>
            <FormControlLabel control={<Switch checked={!!form.active} onChange={(_, v)=>setForm({ ...form, active: v })} />} label="Active" />
          </Stack>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }} color="text.secondary">Basic Info</Typography>
            <Stack spacing={1}>
              <TextField label="Title" value={form.title} onChange={e=>setForm({ ...form, title: e.target.value })} size="small" fullWidth />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField label="Slug" value={form.slug} onChange={e=>setForm({ ...form, slug: e.target.value })} size="small" sx={{ flex: 1 }} />
                <TextField label="Deadline (days)" value={form.deadlineDays} onChange={e=>setForm({ ...form, deadlineDays: Number(e.target.value) })} size="small" sx={{ width: { xs: '100%', sm: 220 } }} />
              </Stack>
              <TextField label="Description" value={form.description} onChange={e=>setForm({ ...form, description: e.target.value })} size="small" multiline minRows={2} />
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }} color="text.secondary">Pricing</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField label="Price" value={form.priceTokens} onChange={e=>setForm({ ...form, priceTokens: Number(e.target.value) })} size="small" sx={{ flex: 1 }} />
              <TextField label="Reward" value={form.rewardTokens} onChange={e=>setForm({ ...form, rewardTokens: Number(e.target.value) })} size="small" sx={{ flex: 1 }} />
              <TextField label="Penalty" value={form.penaltyTokens} onChange={e=>setForm({ ...form, penaltyTokens: Number(e.target.value) })} size="small" sx={{ flex: 1 }} />
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }} color="text.secondary">Availability</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <TextField 
                label="Start Date & Time" 
                type="datetime-local"
                value={form.startAtLocal} 
                onChange={e=>setForm({ ...form, startAtLocal: e.target.value })} 
                size="small" 
                InputLabelProps={{ shrink: true }}
                helperText="Optional - when contract becomes available"
                sx={{ flex: 1 }}
              />
              <TextField 
                label="End Date & Time" 
                type="datetime-local"
                value={form.endAtLocal} 
                onChange={e=>setForm({ ...form, endAtLocal: e.target.value })} 
                size="small" 
                InputLabelProps={{ shrink: true }}
                helperText="When contract becomes unavailable"
                sx={{ flex: 1 }}
              />
            </Stack>
          </Box>

          <Divider sx={{ my: 1 }} />
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1">Tasks</Typography>
            <Button size="small" onClick={addTask}>Add Task</Button>
          </Stack>

          <Stack spacing={1}>
            {form.tasks.map((t, idx) => (
              <Accordion
                key={idx}
                disableGutters
                expanded={expandedTaskIdx === idx}
                onChange={(_, isExpanded) => setExpandedTaskIdx(isExpanded ? idx : -1)}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`task-${idx}`} id={`task-summary-${idx}`}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                    <TextField label="#" value={t.order} onChange={e=>setTask(idx, { order: Number(e.target.value) })} size="small" sx={{ width: 80 }} onClick={e => e.stopPropagation()} />
                    <TextField label="Title" value={t.title} onChange={e=>setTask(idx, { title: e.target.value })} size="small" sx={{ flex: 1 }} onClick={e => e.stopPropagation()} />
                    <IconButton onClick={(e) => { e.stopPropagation(); removeTask(idx); }} size="small"><DeleteIcon fontSize="small" /></IconButton>
                  </Stack>
                </AccordionSummary>
                {expandedTaskIdx === idx && (
                  <AccordionDetails>
                    <Grid container spacing={1}>
                      {criteriaFields.map(([key, label]) => (
                        <Grid item xs={12} sm={6} key={key}>
                          {key === 'sourceCity' || key === 'destinationCity' ? (
                            <Autocomplete freeSolo options={cityOptions.map(city => city.name)} value={(t.criteria||{})[key] || ''}
                              onInputChange={(_, v) => {
                                const selectedCity = cityOptions.find(city => city.name === v);
                                const criteria = {
                                  ...(t.criteria||{}),
                                  [key]: v,
                                  [`${key}Id`]: selectedCity ? selectedCity.id : ''
                                };
                                setTask(idx, { criteria });
                              }}
                              renderInput={(params) => (<TextField {...params} label={label} size="small" fullWidth />)} />
                          ) : key === 'sourceCompany' ? (
                            <Autocomplete freeSolo options={(companyOptionsByCity[(t.criteria||{}).sourceCity || ''] || []).map(company => company.name)} value={(t.criteria||{})[key] || ''}
                              onInputChange={(_, v) => {
                                const selectedCompany = (companyOptionsByCity[(t.criteria||{}).sourceCity || ''] || []).find(company => company.name === v);
                                const criteria = {
                                  ...(t.criteria||{}),
                                  [key]: v,
                                  [`${key}Id`]: selectedCompany ? selectedCompany.id : ''
                                };
                                setTask(idx, { criteria });
                              }}
                              renderInput={(params) => (<TextField {...params} label={label} size="small" fullWidth />)} />
                          ) : key === 'destinationCompany' ? (
                            <Autocomplete freeSolo options={(companyOptionsByCity[(t.criteria||{}).destinationCity || ''] || []).map(company => company.name)} value={(t.criteria||{})[key] || ''}
                              onInputChange={(_, v) => {
                                const selectedCompany = (companyOptionsByCity[(t.criteria||{}).destinationCity || ''] || []).find(company => company.name === v);
                                const criteria = {
                                  ...(t.criteria||{}),
                                  [key]: v,
                                  [`${key}Id`]: selectedCompany ? selectedCompany.id : ''
                                };
                                setTask(idx, { criteria });
                              }}
                              renderInput={(params) => (<TextField {...params} label={label} size="small" fullWidth />)} />
                          ) : key === 'cargoName' ? (
                            <Autocomplete freeSolo options={cargoOptions} value={(t.criteria||{})[key] || ''}
                              onInputChange={(_, v) => {
                                const criteria = { ...(t.criteria||{}), [key]: v };
                                setTask(idx, { criteria });
                              }}
                              renderInput={(params) => (<TextField {...params} label={label} size="small" fullWidth />)} />
                          ) : (
                            <TextField label={label} value={(t.criteria||{})[key] || ''} onChange={e=>{
                              const criteria = { ...(t.criteria||{}), [key]: e.target.value };
                              const numKeys = ['minDistance','maxDamagePct','maxTopSpeedKmh','minRevenue','minAvgSpeedKmh','maxAvgSpeedKmh','maxTruckDamagePercent','maxTrailerDamagePercent'];
                              if (numKeys.includes(key)) criteria[key] = e.target.value === '' ? '' : Number(e.target.value);
                              setTask(idx, { criteria });
                            }} size="small" fullWidth />
                          )}
                        </Grid>
                      ))}
                    </Grid>
                    {t.criteria && (
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mt: 1 }}>
                        {Object.entries(t.criteria).filter(([,v]) => v !== '' && v != null).map(([k,v]) => (
                          <Chip key={k} label={`${k}: ${v}`} size="small" />
                        ))}
                      </Stack>
                    )}
                  </AccordionDetails>
                )}
              </Accordion>
            ))}
          </Stack>
        </Stack>
      </CardContent>
      <CardActions>
        <Button variant="contained" onClick={save}>{editingId ? 'Update' : 'Create'}</Button>
        {editingId && <Button onClick={() => { setEditingId(null); setForm(emptyTemplate); }}>Cancel</Button>}
      </CardActions>
    </Card>
  );

  const renderTemplatesTab = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>Existing Templates</Typography>
        {templates.map(t => (
          <Card key={t._id} variant="outlined" sx={{ mb: 1, p: 1.5 }}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs={12} sm>
                <Stack spacing={0.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle1" fontWeight={700}>{t.title}</Typography>
                    {!t.active && <Chip size="small" label="Inactive" />}
                    {t.approvalStatus === 'pending' && <Chip size="small" label="Pending approval" color="warning" />}
                    {t.approvalStatus === 'rejected' && <Chip size="small" label="Rejected" color="error" />}
                    {(!t.approvalStatus || t.approvalStatus === 'approved') && <Chip size="small" label="Approved" color="success" variant="outlined" />}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">{t.description}</Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mt: 0.5 }}>
                    <Chip size="small" label={`Price ${t.priceTokens}`} />
                    <Chip size="small" label={`Reward ${t.rewardTokens}`} color="success" />
                    <Chip size="small" label={`Penalty ${t.penaltyTokens}`} color="error" />
                    <Chip size="small" label={`Deadline ${t.deadlineDays} days`} />
                  </Stack>
                </Stack>
              </Grid>
              <Grid item>
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => { edit(t); setActiveTab(0); }}>Edit</Button>
                  <IconButton color="error" size="small" onClick={() => del(t._id)}><DeleteIcon /></IconButton>
                </Stack>
              </Grid>
            </Grid>
          </Card>
        ))}
      </CardContent>
    </Card>
  );

  const filteredInstances = useMemo(() => {
    if (!progressSearch.trim()) return instances;
    const q = progressSearch.trim().toLowerCase();
    return instances.filter((c) => {
      const riderName = (c?.riderId?.tmpIngameName || c?.riderId?.name || c?.riderId?.steamName || '').toString().toLowerCase();
      const templateTitle = (c?.templateId?.title || '').toString().toLowerCase();
      return riderName.includes(q) || templateTitle.includes(q);
    });
  }, [instances, progressSearch]);

  const visibleInstances = useMemo(
    () => filteredInstances.slice(0, progressVisibleCount),
    [filteredInstances, progressVisibleCount]
  );
  const hasMoreProgress = filteredInstances.length > progressVisibleCount;

  const progressSummary = useMemo(() => ({
    count: instances.length,
    status: instancesStatus,
  }), [instances.length, instancesStatus]);

  const handleRefreshProgress = () => {
    dispatch(invalidateInstances(statusFilter));
    dispatch(fetchInstancesByStatus(statusFilter));
  };

  const handleManualComplete = async (instanceId, taskOrder) => {
    if (!canMarkDone) {
      setErr('Only admins can mark contract tasks as done');
      return;
    }
    const key = `${instanceId}:${taskOrder}`;
    setCompletingTaskKey(key);
    setErr('');
    setMsg('');
    try {
      await adminCompleteContractTask(instanceId, taskOrder);
      setMsg(`Marked task ${taskOrder} as done`);
      handleRefreshProgress();
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || 'Failed to complete task');
    } finally {
      setCompletingTaskKey(null);
    }
  };

  const handleApproval = async (id, approved) => {
    if (!canApprove) {
      setErr('Only admins can approve or reject contracts');
      return;
    }
    setApprovalActionId(id);
    setErr('');
    setMsg('');
    try {
      let reason;
      if (!approved) {
        reason = window.prompt('Rejection reason (optional):') || '';
      }
      await setContractApproval(id, { approved, reason });
      setMsg(approved ? 'Contract approved — it will go live at its start date/time.' : 'Contract rejected');
      await loadPendingApprovals();
      dispatch(invalidateTemplates());
      await dispatch(fetchTemplates()).unwrap().catch(() => {});
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || 'Approval action failed');
    } finally {
      setApprovalActionId(null);
    }
  };

  const renderApprovalsTab = () => (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Pending publish approvals</Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={loadPendingApprovals} disabled={approvalsLoading}>
              Refresh
            </Button>
          </Stack>
          {!canApprove && (
            <Alert severity="info">Only admins can approve or reject. You can review the queue here.</Alert>
          )}
          {approvalsLoading && <LinearProgress />}
          {!approvalsLoading && pendingApprovals.length === 0 && (
            <Alert severity="info">No contracts awaiting approval.</Alert>
          )}
          {pendingApprovals.map((t) => (
            <Card key={t._id} variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>{t.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{t.description}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Start: {t.startAt ? new Date(t.startAt).toLocaleString() : 'Immediate after approval'}
                    {' · '}
                    End: {t.endAt ? new Date(t.endAt).toLocaleString() : '—'}
                  </Typography>
                </Stack>
                {canApprove && (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      disabled={approvalActionId === t._id}
                      onClick={() => handleApproval(t._id, true)}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      disabled={approvalActionId === t._id}
                      onClick={() => handleApproval(t._id, false)}
                    >
                      Reject
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Card>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );

  const renderProgressTab = () => (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography variant="h6">Contracts Progress</Typography>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <ToggleButtonGroup
                size="small"
                exclusive
                value={statusFilter}
                onChange={(_, v) => { if (v) setStatusFilter(v); }}
              >
                <ToggleButton value="active">Active</ToggleButton>
                <ToggleButton value="completed">Completed</ToggleButton>
                <ToggleButton value="failed">Failed</ToggleButton>
                <ToggleButton value="all">All</ToggleButton>
              </ToggleButtonGroup>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleRefreshProgress}
                disabled={instancesLoading}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ py: 1, px: 1.5, bgcolor: 'action.hover', borderRadius: 1 }} alignItems="center">
            <Typography variant="body2" color="text.secondary">Current filter:</Typography>
            <Typography variant="body2" fontWeight={500} sx={{ textTransform: 'capitalize' }}>
              {statusFilter} — {progressSummary.count} contract{progressSummary.count !== 1 ? 's' : ''}
            </Typography>
          </Stack>

          <TextField
            size="small"
            placeholder="Search by rider or template..."
            value={progressSearch}
            onChange={(e) => setProgressSearch(e.target.value)}
            sx={{ maxWidth: 320 }}
          />

          {instancesLoading && <LinearProgress />}
          {!instancesLoading && instances.length === 0 && (
            <Alert severity="info">No contracts found for the selected filter.</Alert>
          )}

          {!instancesLoading && instances.length > 0 && filteredInstances.length === 0 && (
            <Alert severity="info">No contracts match &quot;{progressSearch}&quot;.</Alert>
          )}

          {!instancesLoading && visibleInstances.length > 0 && (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Showing {visibleInstances.length} of {filteredInstances.length}
                {progressSearch.trim() ? ` (filtered from ${instances.length})` : ''}
              </Typography>
              {visibleInstances.map((c) => {
                const tpl = c.templateId || {};
                const tasks = Array.isArray(tpl.tasks) ? tpl.tasks.slice().sort((a, b) => (a.order || 0) - (b.order || 0)) : [];
                const progress = Array.isArray(c.progress) ? c.progress : [];
                const isDoneStatus = (status) => status === 'done' || status === 'completed';
                let doneCount = 0;
                let total = 1;

                if (tasks.length > 0) {
                  total = tasks.length;
                  const doneOrders = new Set(
                    progress
                      .filter((p) => isDoneStatus(p.status))
                      .map((p) => p.order)
                  );
                  doneCount = tasks.reduce((sum, t) => sum + (doneOrders.has(t.order) ? 1 : 0), 0);
                } else {
                  total = Math.max(progress.length, 1);
                  doneCount = progress.filter((p) => isDoneStatus(p.status)).length;
                }

                const pct = Math.min(100, Math.round((doneCount / total) * 100));
                const riderName = c?.riderId?.tmpIngameName || c?.riderId?.name || c?.riderId?.steamName || c?.riderId?._id || 'Unknown Rider';
                const templateTitle = tpl.title || 'Untitled Template';
                const isExpanded = expandedProgressId === c._id;
                const statusColor = c.status === 'completed' ? 'success' : c.status === 'failed' ? 'error' : 'warning';
                const nextPending = progress.find(p => p.status !== 'done' && p.status !== 'completed');

                return (
                  <Card key={c._id} variant="outlined" sx={{ overflow: 'hidden' }}>
                    <Accordion
                      expanded={isExpanded}
                      onChange={() => setExpandedProgressId(isExpanded ? null : c._id)}
                      disableGutters
                      sx={{ boxShadow: 'none', '&:before': { display: 'none' }, bgcolor: 'transparent' }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.5 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ width: '100%', pr: 1 }}>
                          <Stack sx={{ minWidth: 0, flex: 1 }}>
                            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                              <Typography variant="subtitle1" fontWeight={600}>
                                {riderName}
                              </Typography>
                              <Chip label={c.status} size="small" color={statusColor} sx={{ textTransform: 'capitalize' }} />
                            </Stack>
                            <Typography variant="body2" color="text.secondary">{templateTitle}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Purchased: {c.purchasedAt ? new Date(c.purchasedAt).toLocaleString() : '–'} · Deadline: {c.deadlineAt ? new Date(c.deadlineAt).toLocaleString() : '–'}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 200 }}>
                            <Typography variant="body2" fontWeight={500} sx={{ width: 44, textAlign: 'right' }}>{pct}%</Typography>
                            <LinearProgress variant="determinate" value={pct} sx={{ flex: 1, height: 8, borderRadius: 1 }} color={statusColor} />
                          </Stack>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Task progress</Typography>
                        {c.status === 'active' && nextPending && canMarkDone && (
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 1.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              Current job: <b>{nextPending.order}</b>
                              {(() => {
                                const t = tasks.find(tt => tt.order === nextPending.order);
                                return t?.title ? ` — ${t.title}` : '';
                              })()}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleManualComplete(c._id, nextPending.order)}
                              disabled={completingTaskKey === `${c._id}:${nextPending.order}`}
                            >
                              Mark current job as done
                            </Button>
                          </Stack>
                        )}
                        {c.status === 'active' && nextPending && !canMarkDone && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Current job: {nextPending.order}{(() => {
                              const t = tasks.find(tt => tt.order === nextPending.order);
                              return t?.title ? ` — ${t.title}` : '';
                            })()} (only admins can mark done)
                          </Typography>
                        )}
                        <Stack spacing={0.5}>
                          {tasks.length > 0 ? tasks.map((task, idx) => {
                            const prog = progress.find(pr => pr.order === task.order) ?? progress[idx];
                            const done = isDoneStatus(prog?.status);
                            return (
                              <Stack key={task.order ?? idx} direction="row" alignItems="center" spacing={1}>
                                {done ? <CheckCircleIcon color="success" fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" color="disabled" />}
                                <Typography variant="body2" sx={{ textDecoration: done ? 'line-through' : 'none', color: done ? 'text.secondary' : 'text.primary' }}>
                                  {task.order}. {task.title || `Task ${task.order}`}
                                </Typography>
                                {done && prog?.matchedAt && (
                                  <Typography variant="caption" color="text.secondary">
                                    · Matched {new Date(prog.matchedAt).toLocaleString()}
                                  </Typography>
                                )}
                                {!done && c.status === 'active' && canMarkDone && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleManualComplete(c._id, task.order)}
                                    disabled={completingTaskKey === `${c._id}:${task.order}`}
                                    sx={{ ml: 'auto' }}
                                  >
                                    Mark done
                                  </Button>
                                )}
                              </Stack>
                            );
                          }) : progress.map((p, idx) => (
                            <Stack key={idx} direction="row" alignItems="center" spacing={1}>
                              {isDoneStatus(p.status) ? <CheckCircleIcon color="success" fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" color="disabled" />}
                              <Typography variant="body2">Task {p.order ?? idx + 1}: {p.status}</Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  </Card>
                );
              })}
              {hasMoreProgress && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                  <Button variant="outlined" onClick={() => setProgressVisibleCount(prev => prev + PROGRESS_PAGE_SIZE)}>
                    Load more ({filteredInstances.length - progressVisibleCount} remaining)
                  </Button>
                </Box>
              )}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 2 }}>
      {err && (<Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>)}
      {msg && (<Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert>)}
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Create/Edit" />
          <Tab label="Templates" />
          <Tab label={`Approvals${pendingApprovals.length ? ` (${pendingApprovals.length})` : ''}`} />
          <Tab label="Progress" />
        </Tabs>
      </Box>

      {activeTab === 0 && renderCreateEditTab()}
      {activeTab === 1 && renderTemplatesTab()}
      {activeTab === 2 && renderApprovalsTab()}
      {activeTab === 3 && renderProgressTab()}
    </Box>
  );
}


